import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE, CELL } from '../../shared/geo.js';
import { hash2 } from '../../shared/rng.js';
import { CELLS } from '../../shared/tiles.js';

// Street clutter placed by rule from the map: power poles with sagging wires along the tarred
// roads, water drums beside houses, signposts at shops, kiosks on the shoulders of main roads.
// `propLists` runs in the streaming worker (plain numbers only); `propMeshes` turns the lists into
// one InstancedMesh per kind on the main thread, so a cell's clutter costs at most four draw calls.

const POLE_SPACING = 35; // metres between power poles
const LAMP_SPACING = 32; // metres between streetlights, where there are any
const KIOSK_SPACING = 140;
const SHOULDER_OUT = 1.6 + 0.5; // shoulder and gutter, as roads.js lays them

export const KINDS = ['pole', 'drum', 'sign', 'kiosk', 'lamp'];
// instance tints by kind: index into these (white keeps the vertex colours)
export const TINTS = {
  pole: ['#ffffff'],
  drum: ['#2f4fa0', '#1c1c1e', '#2f8a56', '#c8402f'],
  sign: ['#f2b705', '#c8402f', '#2f6fb4', '#2f8a56', '#e07a28', '#f4f1e8'],
  kiosk: ['#f2b705', '#2f6fb4', '#2f8a56', '#e07a28', '#c8402f'],
  lamp: ['#ffffff'],
};

function boxes(data) {
  return data.b.map((b) => {
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < b.p.length; i += 2) {
      const x = b.p[i] / 10;
      const z = b.p[i + 1] / 10;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (z < z0) z0 = z;
      if (z > z1) z1 = z;
    }
    return { x0, z0, x1, z1, k: b.k };
  });
}

function segments(data) {
  const segs = [];
  data.r.forEach((r, index) => {
    for (let i = 2; i < r.p.length; i += 2) segs.push({ ax: r.p[i - 2] / 10, az: r.p[i - 1] / 10, bx: r.p[i] / 10, bz: r.p[i + 1] / 10, half: r.w / 2, c: r.c, s: r.s, index });
  });
  return segs;
}

function roadDistance(x, z, segs, skipIndex = -1) {
  let best = Infinity;
  let at = null;
  for (const s of segs) {
    if (s.index === skipIndex) continue;
    const ex = s.bx - s.ax;
    const ez = s.bz - s.az;
    let u = ((x - s.ax) * ex + (z - s.az) * ez) / (ex * ex + ez * ez || 1);
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const px = s.ax + ex * u;
    const pz = s.az + ez * u;
    const d = Math.hypot(x - px, z - pz) - s.half;
    if (d < best) {
      best = d;
      at = { px, pz, s };
    }
  }
  return { d: best, at };
}

function nearBuilding(x, z, rects, pad) {
  for (const r of rects) if (x > r.x0 - pad && x < r.x1 + pad && z > r.z0 - pad && z < r.z1 + pad) return true;
  return false;
}

// Where the clutter of a tile stands (or of one of its cells when cx, cz are given): one flat list
// per kind of [x, y, z, yaw, tint] with x and z local to the tile's corner, plus the wire spans
// between poles as [x0, y0, z0, x1, y1, z1] and the streetlights' heads as [x, y, z, yaw].
// lamps: { x, z, radius } in world metres — streetlights stand only on the main roads within that
// circle (the town centre); most of the region has none, as the art bible says.
export function propLists(data, tx, tz, heightmap, cx = null, cz = null, lamps = null) {
  const lists = { pole: [], drum: [], sign: [], kiosk: [], lamp: [], wires: [], heads: [] };
  if (!data) return lists;
  const ox = tx * TILE;
  const oz = tz * TILE;
  const inCell = (x, z) => cx === null || (Math.floor(x / CELL) === cx && Math.floor(z / CELL) === cz);
  const rects = boxes(data);
  const segs = segments(data);
  const groundAt = (x, z) => heightmap.heightAt(ox + x, oz + z);

  // poles: along every tarred road of class 3 and better, on its right, beyond the gutter
  data.r.forEach((r, index) => {
    if (r.s !== 0 || r.c > 3 || r.p.length < 4) return;
    const offset = r.w / 2 + SHOULDER_OUT + 0.7;
    let dist = 0;
    let next = POLE_SPACING * hash2(index, tx * 31 + tz, 51); // a different start on every road
    let prev = null;
    for (let i = 2; i < r.p.length; i += 2) {
      const ax = r.p[i - 2] / 10;
      const az = r.p[i - 1] / 10;
      const bx = r.p[i] / 10;
      const bz = r.p[i + 1] / 10;
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 0.01) continue;
      const dx = (bx - ax) / len;
      const dz = (bz - az) / len;
      while (next <= dist + len) {
        const t = (next - dist) / len;
        const x = ax + dx * len * t - dz * offset;
        const z = az + dz * len * t + dx * offset;
        next += POLE_SPACING;
        if (x < 0 || z < 0 || x >= TILE || z >= TILE) {
          prev = null;
          continue;
        }
        if (nearBuilding(x, z, rects, 0.6) || roadDistance(x, z, segs, index).d < 0.8) {
          prev = null;
          continue;
        }
        const y = groundAt(x, z);
        const yaw = Math.atan2(dx, dz);
        if (inCell(x, z)) lists.pole.push(x, y, z, yaw, 0);
        // the wires belong to the cell of the pole they start from
        if (prev && inCell(prev[0], prev[2]) && Math.hypot(x - prev[0], z - prev[2]) < POLE_SPACING * 1.6) lists.wires.push(prev[0], prev[1] + 7.6, prev[2], x, y + 7.6, z);
        prev = [x, y, z];
      }
      dist += len;
    }
  });

  // streetlights: on the left of main roads in the town centre, opposite the power poles
  if (lamps) {
    data.r.forEach((r, index) => {
      if (r.s !== 0 || r.c > 2 || r.p.length < 4) return;
      const offset = -(r.w / 2 + SHOULDER_OUT + 0.5);
      let dist = 0;
      let next = LAMP_SPACING * hash2(index, tx * 13 + tz, 55);
      for (let i = 2; i < r.p.length; i += 2) {
        const ax = r.p[i - 2] / 10;
        const az = r.p[i - 1] / 10;
        const bx = r.p[i] / 10;
        const bz = r.p[i + 1] / 10;
        const len = Math.hypot(bx - ax, bz - az);
        if (len < 0.01) continue;
        const dx = (bx - ax) / len;
        const dz = (bz - az) / len;
        while (next <= dist + len) {
          const t = (next - dist) / len;
          next += LAMP_SPACING;
          // the left of the road first; where that is another carriageway (a dual road's median),
          // the right, a step beyond the power poles
          for (const side of [offset, -offset + 1.2]) {
            const x = ax + dx * len * t - dz * side;
            const z = az + dz * len * t + dx * side;
            if (x < 0 || z < 0 || x >= TILE || z >= TILE || !inCell(x, z)) continue;
            if (Math.hypot(ox + x - lamps.x, oz + z - lamps.z) > lamps.radius) continue;
            if (nearBuilding(x, z, rects, 0.6) || roadDistance(x, z, segs, index).d < 0.8) continue;
            const y = groundAt(x, z);
            // the arm reaches over the road: the lamp faces the carriageway
            const yaw = Math.atan2(dx, dz) + (side < 0 ? Math.PI / 2 : -Math.PI / 2);
            lists.lamp.push(x, y, z, yaw, 0);
            lists.heads.push(x - Math.sin(yaw) * 1.6, y + 8.75, z - Math.cos(yaw) * 1.6, yaw);
            break;
          }
        }
        dist += len;
      }
    });
  }

  // kiosks: on the shoulder of main roads, every so often, alternating sides
  data.r.forEach((r, index) => {
    if (r.s !== 0 || r.c > 2 || r.p.length < 4) return;
    let dist = 0;
    let next = KIOSK_SPACING * (0.3 + hash2(index, tx * 7 + tz, 52) * 0.7);
    let side = hash2(index, 1, 53) < 0.5 ? 1 : -1;
    for (let i = 2; i < r.p.length; i += 2) {
      const ax = r.p[i - 2] / 10;
      const az = r.p[i - 1] / 10;
      const bx = r.p[i] / 10;
      const bz = r.p[i + 1] / 10;
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 0.01) continue;
      const dx = (bx - ax) / len;
      const dz = (bz - az) / len;
      while (next <= dist + len) {
        const t = (next - dist) / len;
        const offset = (r.w / 2 + SHOULDER_OUT + 1.6) * side;
        const x = ax + dx * len * t - dz * offset;
        const z = az + dz * len * t + dx * offset;
        next += KIOSK_SPACING;
        side = -side;
        if (x < 0 || z < 0 || x >= TILE || z >= TILE || !inCell(x, z)) continue;
        if (nearBuilding(x, z, rects, 2.5) || roadDistance(x, z, segs, index).d < 2) continue;
        const yaw = Math.atan2(dx, dz) + (side > 0 ? Math.PI / 2 : -Math.PI / 2); // facing the road
        lists.kiosk.push(x, groundAt(x, z), z, yaw, Math.floor(hash2(x | 0, z | 0, 54) * TINTS.kiosk.length));
      }
      dist += len;
    }
  });

  // drums beside houses, signposts in front of shops
  rects.forEach((r, i) => {
    const seed = tx * 7919 + tz * 104729 + i * 31;
    const w = r.x1 - r.x0;
    const d = r.z1 - r.z0;
    if (w < 3 || d < 3) return;
    if (r.k === 0 && hash2(seed, 21, 3) < 0.3) {
      // at a corner, a little out from the wall
      const corner = Math.floor(hash2(seed, 22, 3) * 4);
      const x = (corner & 1 ? r.x1 + 0.6 : r.x0 - 0.6) + (hash2(seed, 23, 3) - 0.5) * 0.6;
      const z = (corner & 2 ? r.z1 + 0.6 : r.z0 - 0.6) + (hash2(seed, 24, 3) - 0.5) * 0.6;
      if (x > 0 && z > 0 && x < TILE && z < TILE && inCell(x, z) && roadDistance(x, z, segs).d > 1 && !nearBuilding(x, z, rects, 0.2)) {
        const count = 1 + (hash2(seed, 25, 3) < 0.4 ? 1 : 0);
        for (let k = 0; k < count; k++) {
          const px = x + k * 0.7 * (corner & 1 ? 1 : -1);
          lists.drum.push(px, groundAt(px, z), z, hash2(seed, 26 + k, 3) * Math.PI * 2, Math.floor(hash2(seed, 27 + k, 3) * TINTS.drum.length));
        }
      }
    }
    if (r.k === 1 && hash2(seed, 28, 3) < 0.6) {
      const { d: dist, at } = roadDistance((r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2, segs);
      if (!at || dist > 20) return;
      // on the shoulder, between the shop and the road, facing the road
      const s = at.s;
      const ex = s.bx - s.ax;
      const ez = s.bz - s.az;
      const len = Math.hypot(ex, ez) || 1;
      let nx = ez / len;
      let nz = -ex / len;
      const mx = (r.x0 + r.x1) / 2 - at.px;
      const mz = (r.z0 + r.z1) / 2 - at.pz;
      if (nx * mx + nz * mz < 0) {
        nx = -nx;
        nz = -nz;
      }
      const x = at.px + nx * (s.half + SHOULDER_OUT + 0.4);
      const z = at.pz + nz * (s.half + SHOULDER_OUT + 0.4);
      if (x > 0 && z > 0 && x < TILE && z < TILE && inCell(x, z) && !nearBuilding(x, z, rects, 0.3)) {
        lists.sign.push(x, groundAt(x, z), z, Math.atan2(-nx, -nz), Math.floor(hash2(seed, 29, 3) * TINTS.sign.length));
      }
    }
  });
  return lists;
}

// Sagging wires between poles, as thin dark quads in a MeshBuilder (the walls' material).
export function addWires(builder, wires, colour, uv) {
  const SEGS = 5;
  const prev = [null, null, null];
  for (let i = 0; i < wires.length; i += 6) {
    const x0 = wires[i];
    const y0 = wires[i + 1];
    const z0 = wires[i + 2];
    const x1 = wires[i + 3];
    const y1 = wires[i + 4];
    const z1 = wires[i + 5];
    const len = Math.hypot(x1 - x0, z1 - z0) || 1;
    const nx = ((z1 - z0) / len) * 0.45;
    const nz = (-(x1 - x0) / len) * 0.45;
    for (let w = -1; w <= 1; w++) {
      prev[w + 1] = null;
      for (let k = 0; k <= SEGS; k++) {
        const t = k / SEGS;
        const sag = 0.7 * 4 * t * (1 - t);
        const x = x0 + (x1 - x0) * t + nx * w;
        const y = y0 + (y1 - y0) * t - sag;
        const z = z0 + (z1 - z0) * t + nz * w;
        const p = prev[w + 1];
        if (p) {
          builder.quad([p[0], p[1], p[2]], [x, y, z], [x, y + 0.04, z], [p[0], p[1] + 0.04, p[2]], colour, uv);
          builder.quad([x, y, z], [p[0], p[1], p[2]], [p[0], p[1] + 0.04, p[2]], [x, y + 0.04, z], colour, uv);
        }
        prev[w + 1] = [x, y, z];
      }
    }
  }
}

// The glowing heads of the streetlights, as small boxes drawn with the window glass so they light
// up with the windows after dark (and go out with them in a power cut).
export function addLampHeads(builder, heads, colour, uv) {
  const h = 0.22;
  const w = 0.6;
  const d = 0.3;
  for (let i = 0; i < heads.length; i += 4) {
    const x = heads[i];
    const y = heads[i + 1];
    const z = heads[i + 2];
    const yaw = heads[i + 3];
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const P = (lx, ly, lz) => [x + lx * c + lz * s, y + ly, z - lx * s + lz * c];
    const corners = [
      [P(-w / 2, 0, -d / 2), P(w / 2, 0, -d / 2), P(w / 2, h, -d / 2), P(-w / 2, h, -d / 2)],
      [P(w / 2, 0, d / 2), P(-w / 2, 0, d / 2), P(-w / 2, h, d / 2), P(w / 2, h, d / 2)],
      [P(-w / 2, 0, d / 2), P(-w / 2, 0, -d / 2), P(-w / 2, h, -d / 2), P(-w / 2, h, d / 2)],
      [P(w / 2, 0, -d / 2), P(w / 2, 0, d / 2), P(w / 2, h, d / 2), P(w / 2, h, -d / 2)],
      [P(-w / 2, 0, d / 2), P(w / 2, 0, d / 2), P(w / 2, 0, -d / 2), P(-w / 2, 0, -d / 2)],
    ];
    for (const [a, b, cc, dd] of corners) builder.quad(a, b, cc, dd, colour, uv);
  }
}

// ---------------------------------------------------------------------------------------------
// The shapes, made once on the main thread

function tinted(geometry, hex) {
  const c = new THREE.Color(hex);
  const n = geometry.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return geometry.index ? geometry.toNonIndexed() : geometry;
}
const part = (g, hex, x, y, z) => tinted(g.translate(x, y, z), hex);

let SHAPES = null;
let MATERIAL = null;
const dummy = new THREE.Object3D();
const SHARED_PROGRAM = 1025; // see trees.js: past 1024 instances the new renderer shares one shader
const tintColours = {};

export function propMaterial() {
  if (!SHAPES) {
    SHAPES = {
      pole: mergeGeometries([part(new THREE.CylinderGeometry(0.1, 0.14, 8, 6), '#9a9a96', 0, 4, 0), part(new THREE.BoxGeometry(1.4, 0.1, 0.1), '#6b5a48', 0, 7.6, 0), part(new THREE.BoxGeometry(0.1, 0.16, 0.1), '#d8d8d4', 0.55, 7.73, 0), part(new THREE.BoxGeometry(0.1, 0.16, 0.1), '#d8d8d4', -0.55, 7.73, 0)]),
      // a drum is tinted per instance, so its colours start white; the lid darker
      drum: mergeGeometries([part(new THREE.CylinderGeometry(0.3, 0.3, 0.9, 10), '#ffffff', 0, 0.45, 0), part(new THREE.CylinderGeometry(0.31, 0.31, 0.03, 10), '#8c8c8c', 0, 0.9, 0)]),
      // a signpost: dark post, board tinted per instance
      sign: mergeGeometries([part(new THREE.BoxGeometry(0.08, 2.4, 0.08), '#4a4a48', 0, 1.2, 0), part(new THREE.BoxGeometry(1.2, 0.8, 0.05), '#ffffff', 0, 2.1, 0)]),
      // a kiosk: tinted body, dark hatch, zinc lean-to
      kiosk: mergeGeometries([part(new THREE.BoxGeometry(1.8, 2.1, 1.5), '#ffffff', 0, 1.05, 0), part(new THREE.BoxGeometry(1.1, 0.7, 0.06), '#2a2a2e', 0, 1.35, 0.76), part(new THREE.BoxGeometry(2.2, 0.06, 1.9), '#a9aeb2', 0, 2.2, 0.1)]),
      // a streetlight: a galvanised pole with an arm reaching over the road (its glowing head is drawn with the walls)
      lamp: mergeGeometries([part(new THREE.CylinderGeometry(0.07, 0.11, 8.6, 6), '#8f9398', 0, 4.3, 0), part(new THREE.BoxGeometry(0.1, 0.1, 1.7), '#8f9398', 0, 8.6, -0.8)]),
    };
    MATERIAL = new THREE.MeshLambertMaterial({ vertexColors: true });
    for (const [kind, hexes] of Object.entries(TINTS)) tintColours[kind] = hexes.map((h) => new THREE.Color(h));
  }
  return MATERIAL;
}

// The props of propLists as one InstancedMesh per kind in a Group at world (ox, oz), or null.
export function propMeshes(lists, ox, oz) {
  propMaterial();
  let group = null;
  for (const kind of KINDS) {
    const list = lists[kind];
    if (!list || list.length === 0) continue;
    const count = list.length / 5;
    const mesh = new THREE.InstancedMesh(SHAPES[kind], MATERIAL, Math.max(count, SHARED_PROGRAM));
    mesh.count = count;
    for (let i = 0; i < count; i++) {
      dummy.position.set(list[i * 5], list[i * 5 + 1] - 0.05, list[i * 5 + 2]);
      dummy.rotation.set(0, list[i * 5 + 3], 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, tintColours[kind][list[i * 5 + 4] % tintColours[kind].length]);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor.needsUpdate = true;
    mesh.castShadow = true;
    mesh.computeBoundingSphere();
    if (!group) {
      group = new THREE.Group();
      group.position.set(ox, 0, oz);
    }
    group.add(mesh);
  }
  return group;
}

export { CELLS };

// The pool of light each streetlight throws on the ground: a flat quad under the head, the
// radial glow in its texture, drawn additively and faded in with the dark (materials.pools).
// heads: [x, y, z, yaw] as propLists gives them (y is the head, 8.75 m above the pole's foot).
export function addLampPools(builder, heads, colour, groundAt, radius = 7) {
  const N = 12; // rim points: the pool follows the ground, which slopes under a road
  for (let i = 0; i < heads.length; i += 4) {
    const x = heads[i];
    const z = heads[i + 2];
    const centre = [x, groundAt(x, z) + 0.3, z];
    const rim = [];
    for (let k = 0; k < N; k++) {
      const a = (k / N) * Math.PI * 2;
      const rx = x + Math.cos(a) * radius;
      const rz = z + Math.sin(a) * radius;
      rim.push({ p: [rx, groundAt(rx, rz) + 0.3, rz], uv: [0.5 + 0.5 * Math.cos(a), 0.5 + 0.5 * Math.sin(a)] });
    }
    for (let k = 0; k < N; k++) {
      const a = rim[k];
      const b = rim[(k + 1) % N];
      builder.tri(centre, b.p, a.p, colour, [0.5, 0.5], b.uv, a.uv);
    }
  }
}
